if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.onBreakpointChange = this.onBreakpointChange.bind(this);
      }

      connectedCallback() {
        if (this.dataset.swiper === 'true') {
          this.initSwipers();
          this.mql.addEventListener('change', this.onBreakpointChange);
          return;
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        this.mql.removeEventListener('change', this.onBreakpointChange);
        this.resizeObserver?.disconnect();
        this.destroySwipers();
      }

      onBreakpointChange() {
        if (this.dataset.swiper !== 'true') return;
        this.initSwipers();
      }

      initSwipers() {
        if (typeof window.Swiper === 'undefined') return;

        this.destroySwipers();

        const mainEl = this.querySelector('.product-media-swiper__main');
        const thumbsEl = this.querySelector('.product-media-swiper__thumbs');
        if (!mainEl) return;

        const isDesktop = this.mql.matches;
        const slideCount = mainEl.querySelectorAll('.swiper-slide').length;

        if (thumbsEl && slideCount > 1) {
          this.thumbsSwiper = new window.Swiper(thumbsEl, {
            direction: isDesktop ? 'vertical' : 'horizontal',
            slidesPerView: isDesktop ? 'auto' : Math.min(4, slideCount),
            spaceBetween: isDesktop ? 12 : 8,
            watchSlidesProgress: true,
            watchOverflow: true,
            slideToClickedSlide: true,
            threshold: 5,
          });
          this.syncThumbsHeight(mainEl, thumbsEl, isDesktop);
        }

        this.mainSwiper = new window.Swiper(mainEl, {
          slidesPerView: 1,
          spaceBetween: 0,
          watchOverflow: true,
          threshold: 5,
          thumbs:
            this.thumbsSwiper && !this.thumbsSwiper.destroyed
              ? { swiper: this.thumbsSwiper, autoScrollOffset: 1 }
              : undefined,
          pagination: {
            el: this.querySelector('.product-media-swiper__pagination'),
            clickable: true,
          },
          on: {
            slideChange: () => this.onMainSlideChange(),
          },
        });

        this.resizeObserver?.disconnect();
        this.resizeObserver = new ResizeObserver(() => {
          this.syncThumbsHeight(mainEl, thumbsEl, this.mql.matches);
          this.thumbsSwiper?.update();
          this.mainSwiper?.update();
        });
        this.resizeObserver.observe(mainEl);
      }

      syncThumbsHeight(mainEl, thumbsEl, isDesktop) {
        if (!thumbsEl) return;
        const wrap = thumbsEl.closest('.product-media-swiper__thumbs-wrap') || thumbsEl;
        wrap.style.height = '';
        thumbsEl.style.height = '';

        if (!isDesktop || !mainEl) return;

        const slide = thumbsEl.querySelector('.swiper-slide');
        const slideCount = thumbsEl.querySelectorAll('.swiper-slide').length;
        const slideHeight = slide ? slide.getBoundingClientRect().height : 0;
        const gap = 12;
        const naturalHeight = slideHeight * slideCount + gap * Math.max(0, slideCount - 1);
        const maxHeight = mainEl.offsetHeight;

        if (naturalHeight > maxHeight && maxHeight > 0) {
          thumbsEl.style.height = `${maxHeight}px`;
        }
      }

      destroySwipers() {
        this.mainSwiper?.destroy(true, true);
        this.thumbsSwiper?.destroy(true, true);
        this.mainSwiper = null;
        this.thumbsSwiper = null;
      }

      onMainSlideChange() {
        const activeMedia = this.mainSwiper?.slides?.[this.mainSwiper.activeIndex];
        if (!activeMedia) return;
        this.syncActiveThumb(activeMedia.dataset.mediaId);
        this.playActiveMedia(activeMedia);
      }

      syncActiveThumb(mediaId) {
        if (!mediaId) return;

        const thumbs = this.querySelectorAll('.product-media-swiper__thumbs .swiper-slide');
        let activeIndex = -1;

        thumbs.forEach((thumb, index) => {
          const isActive = thumb.dataset.mediaId === mediaId;
          thumb.classList.toggle('swiper-slide-thumb-active', isActive);
          if (isActive) activeIndex = index;
        });

        if (activeIndex >= 0 && this.thumbsSwiper && !this.thumbsSwiper.destroyed) {
          this.thumbsSwiper.slideTo(activeIndex, 0);
        }
      }

      syncThumbsOrder() {
        const thumbsWrapper = this.querySelector('.product-media-swiper__thumbs .swiper-wrapper');
        if (!thumbsWrapper) return;

        this.querySelectorAll('.product-media-swiper__main .swiper-slide').forEach((slide) => {
          const thumb = thumbsWrapper.querySelector(`[data-media-id="${slide.dataset.mediaId}"]`);
          if (thumb) thumbsWrapper.appendChild(thumb);
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        if (this.mainSwiper) {
          if (prepend) this.syncThumbsOrder();
          this.mainSwiper.update();
          this.thumbsSwiper?.update();

          const index = [...this.mainSwiper.slides].findIndex((slide) => slide.dataset.mediaId === mediaId);
          if (index >= 0) {
            this.mainSwiper.slideTo(index, 0);
            this.thumbsSwiper?.slideTo(index, 0);
          }

          this.syncActiveThumb(mediaId);
          this.mainSwiper.thumbs?.update?.(true);

          const activeMedia = this.mainSwiper.slides[this.mainSwiper.activeIndex];
          if (activeMedia) this.playActiveMedia(activeMedia);
          return;
        }

        const activeMedia =
          this.elements.viewer?.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer?.querySelector('[data-media-id]');
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
